import { FC, FocusEvent, SyntheticEvent, useEffect, useMemo, useState } from 'react'
import { useRouter } from 'next/router'
import { FlexboxGrid, InputNumber } from 'rsuite'
import omit from 'lodash/omit'
import { useConfigure, useHits } from 'react-instantsearch'

import useUrls from '../../../services/useUrls'
import { ProductAttribute } from '../../../utils/types/Product'
import { debounce, disableEditOnScroll } from '../../../utils/event-utils'
import { getPickerCode, getQueryValue } from './util'
import FilterSelectionIcon from '../../Icons/FilterSelectionIcon'
import BaseFilterPicker from './BaseFilterPicker'
import { QueryParam } from '../../../utils/constants'
import useGlobalAnalyticsParams from '../../hooks/useGlobalAnalyticsParams'
import { findFeatureValue, isFilterAttribute } from '../../../lib/resources/attribute'
import { findByType, getValue, PropertyType } from '../../../lib/property'
import { getCountryAndLocaleStrings } from '../../../utils/locales'
import { not } from '../../../utils/util'
import { FilterItem } from '../../../utils/types/misc'
import useIsOnMobile from '../../../services/useIsOnMobile'

import styles from '../../../styles/CatalogFilters.module.less'

interface NumericFilterPickerProps {
  filter: ProductAttribute
  shouldCloseFilters: boolean
  appliedFilters?: Record<string, FilterItem[]>
  updateAppliedFilter?: (
    filterKey: string,
    filterItem: FilterItem,
    isSingleSelect?: boolean
  ) => void
  removeAppliedFilter?: (filterKey: string) => void
}

const NumericFilterPicker: FC<NumericFilterPickerProps> = (props) => {
  const {
    filter,
    shouldCloseFilters,
    appliedFilters = {},
    updateAppliedFilter,
    removeAppliedFilter,
  } = props

  const { pushT } = useUrls()
  const isOnMobile = useIsOnMobile()
  const { query, asPath, locale } = useRouter()
  const { locale: lang } = getCountryAndLocaleStrings(locale)
  const [globalAnalyticsParams, setGlobalAnalyticsParams] = useGlobalAnalyticsParams()
  const { filterInteractionAmount = 0 } = globalAnalyticsParams
  const filtersData = filter?.attribute?.filter(not(isFilterAttribute))

  const splitLabel = findFeatureValue(filtersData)
    ?.['attribute.label'].split('#').at(-1) ?? ''
  const scaleUnit = findByType(filter, PropertyType.scaleUnit)
  const unit = scaleUnit ? getValue(scaleUnit) : splitLabel
  const { results } = useHits()
  const facetStats = results?.getFacetStats(`feature-types.${lang}.${filter['attribute.label']} ${unit}`)
  const min: number | undefined = Math.ceil(facetStats?.min * 100) / 100 || 0
  const max: number | undefined = Math.ceil(facetStats?.max * 100) / 100 || 0

  const filterCode = getPickerCode(filter)

  // Get values from query params for initialization
  const queryMinValue = getQueryValue(query, `${filterCode}-min`)
  const queryMaxValue = getQueryValue(query, `${filterCode}-max`)

  // Use appliedFilters for controlling the component if provided
  const minValue = useMemo(() => {
    if (appliedFilters[`${filterCode}-min`]?.length) {
      return String(appliedFilters[`${filterCode}-min`][0].value)
    }
    return queryMinValue
  }, [appliedFilters, filterCode, queryMinValue])

  const maxValue = useMemo(() => {
    if (appliedFilters[`${filterCode}-max`]?.length) {
      return String(appliedFilters[`${filterCode}-max`][0].value)
    }
    return queryMaxValue
  }, [appliedFilters, filterCode, queryMaxValue])

  // Add local state to track input values for immediate UI updates
  const [localMinValue, setLocalMinValue] = useState<string | undefined>(minValue || undefined)
  const [localMaxValue, setLocalMaxValue] = useState<string | undefined>(maxValue || undefined)

  // Update local state when props change
  useEffect(() => {
    if (!minValue) {
      setLocalMinValue('')
    }
  }, [minValue])

  useEffect(() => {
    if (!maxValue) {
      setLocalMaxValue('')
    }
  }, [maxValue])

  const refinementFilter = {
    '<=': maxValue ? [Number(maxValue)] : [],
    '>=': minValue ? [Number(minValue)] : [],
  }

  // Use useConfigure component instead of useRange hook since
  // the numeric isntantsearch hook does not seem to work nicely with typesense
  useConfigure({
    disjunctiveFacets: [`feature-types.${lang}.${filter['attribute.label']} ${unit}`],
    numericRefinements: {
      [`feature-types.${lang}.${filter['attribute.label']} ${unit}`]: refinementFilter,
    },
  })

  // Initialize appliedFilters state from URL on page load
  useEffect(() => {
    if (query) {
      // For min value
      if (queryMinValue && updateAppliedFilter) {
        updateAppliedFilter(`${filterCode}-min`, {
          label: `${filter['attribute.label']} min: ${queryMinValue} ${unit}`,
          value: Number(queryMinValue),
        }, true)
      }

      // For max value
      if (queryMaxValue && updateAppliedFilter) {
        updateAppliedFilter(`${filterCode}-max`, {
          label: `${filter['attribute.label']} max: ${queryMaxValue} ${unit}`,
          value: Number(queryMaxValue),
        }, true)
      }
    }
  }, [query, queryMinValue, queryMaxValue])

  const debouncedUpdateFilter = (range: 'min' | 'max') => debounce((newValue: string | number) => {
    const filterKey = `${filterCode}-${range}`
    const currentQueries = omit(query, [filterKey, QueryParam.filters, QueryParam.supplierCode])
    const clampedValue = range === 'max' ? Math.max(Number(newValue), min) : Math.min(Number(newValue), max)

    // Update applied filters if value is set
    if (newValue && updateAppliedFilter) {
      updateAppliedFilter(filterKey, {
        label: `${filter['attribute.label']} ${range}: ${clampedValue} ${unit}`,
        value: clampedValue,
      }, true)
    } else if (!newValue && removeAppliedFilter) {
      removeAppliedFilter(filterKey)
    }

    pushT({
      pathname: asPath.split('?')?.[0],
      query: !newValue
        ? currentQueries
        : { ...currentQueries, [filterKey]: clampedValue },
    }, undefined, { shallow: true })
  }, 1000)

  const handleInputChange = (range: 'min' | 'max') => (newValue: string | number) => {
    // Update local state immediately for responsive UI
    if (range === 'min') {
      setLocalMinValue(newValue?.toString() || undefined)
    } else {
      setLocalMaxValue(newValue?.toString() || undefined)
    }

    // Debounce the actual filter update
    debouncedUpdateFilter(range)(newValue)
  }

  const handleOnBlur = (range: 'min' | 'max') => (input: FocusEvent<HTMLInputElement>) => {
    setGlobalAnalyticsParams({
      ...globalAnalyticsParams,
      selectedFilterValue: input.target.value,
      selectedFilterCode: `${filterCode}-${range}`,
      filterInteractionAmount: Number(filterInteractionAmount) + 1,
    })
  }

  const handleClearInputs = (event: SyntheticEvent<Element, Event>) => {
    const currentQueries = omit(query, [`${filterCode}-min`, `${filterCode}-max`, QueryParam.filters])
    if (event.type === 'click') { // if cross button pressed
      // Clear filter from applied filters
      if (removeAppliedFilter) {
        removeAppliedFilter(`${filterCode}-min`)
        removeAppliedFilter(`${filterCode}-max`)
      }

      pushT({
        pathname: asPath.split('?')?.[0],
        query: currentQueries,
      }, undefined, { shallow: true })

      // Also clear local state
      setLocalMinValue(undefined)
      setLocalMaxValue(undefined)
    }
  }

  const renderValue = () => (
    <FlexboxGrid
      align="middle"
      justify="space-between"
      className="ellipsis flex-flow-row"
    >
      <div className="ellipsis">{filter['attribute.label']}</div>
      <div className="flex-direction-column margin-left-spacer-half">
        <FilterSelectionIcon />
      </div>
    </FlexboxGrid>
  )

  const renderNumericInputs = () => {
    const placeholderMin = filterCode === 'f_palm-thickness' ? '0.00' : min.toFixed(2)
    const placeholderMax = filterCode === 'f_palm-thickness' ? '0.50' : max.toFixed(2)

    return (
      <FlexboxGrid
        align="middle"
        className={`${styles['numeric-filter-inputs']} flex-flow-row`}
      >

        <InputNumber
          postfix={unit}
          placeholder={placeholderMin}
          min={0}
          max={max}
          value={localMinValue || ''}
          onChange={handleInputChange('min')}
          onBlur={handleOnBlur('min')}
          onWheelCapture={disableEditOnScroll}
        />
        <div className={styles.line} />
        <InputNumber
          postfix={unit}
          min={min}
          placeholder={placeholderMax}
          value={localMaxValue || ''}
          onChange={handleInputChange('max')}
          onBlur={handleOnBlur('max')}
          onWheelCapture={disableEditOnScroll}
        />
      </FlexboxGrid>
    )
  }

  if (isOnMobile) {
    return renderNumericInputs()
  }

  return (
    <BaseFilterPicker
      data={[]} // as we have to provide this prop although we dont need to render any list
      filterCode={filterCode}
      renderValue={renderValue}
      value={minValue || maxValue ? [splitLabel] : []}
      placeholder={filter['attribute.label']}
      searchable={false}
      isSort={false}
      shouldCloseFilters={shouldCloseFilters}
      renderMenu={renderNumericInputs}
      onClean={handleClearInputs}
    />
  )
}

export default NumericFilterPicker
